import { App as AntdApp, ConfigProvider, theme } from "antd";
import ruRU from "antd/locale/ru_RU";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import type { ReactNode } from "react";

dayjs.locale("ru");

/**
 * Тема и локаль.
 *
 * Плотность задаётся здесь и только здесь — на страницах `size="small"` быть
 * не должно. Базовый размер 14px (дефолт antd), контролы 32px: с прежними
 * 16px и 40px интерфейс выглядел крупнее нужного, а в таблицы заявок влезало
 * заметно меньше строк. `compactAlgorithm` при этом не включён намеренно — он
 * ушёл бы ещё на ступень ниже (12px, контролы 28) и режет шаг отступов вдвое,
 * а админкой пользуются по многу часов подряд.
 *
 * `AntdApp` оборачивает всё ради `App.useApp()`: статические `message.error` и
 * `Modal.confirm` не видят ConfigProvider и рендерятся мимо темы.
 *
 * `cssVar` включает CSS-переменные темы — на них ссылаются компоненты
 * (`var(--ant-color-success)`), чтобы не хардкодить палитру, которая в тёмной
 * теме другая.
 */
export function AntdProvider({
  children,
  mode,
}: {
  children: ReactNode;
  mode: "light" | "dark";
}) {
  const dark = mode === "dark";

  return (
    <ConfigProvider
      locale={ruRU}
      theme={{
        algorithm: dark ? theme.darkAlgorithm : theme.defaultAlgorithm,
        cssVar: { prefix: "ant" },
        token: {
          // «Inter Variable» — именно так пакет @fontsource-variable/inter
          // объявляет семейство. С именем «Inter» шрифт молча не применяется.
          fontFamily:
            "'Inter Variable', Inter, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
          fontSize: 14,
          fontSizeSM: 12,
          fontSizeLG: 16,
          fontSizeXL: 18,
          fontSizeHeading1: 28,
          fontSizeHeading2: 23,
          fontSizeHeading3: 19,
          fontSizeHeading4: 16,
          fontSizeHeading5: 15,
          lineHeight: 1.5,
          controlHeight: 32,
          controlHeightSM: 26,
          controlHeightLG: 40,
          borderRadius: 8,
          borderRadiusLG: 11,
          borderRadiusSM: 6,
          // Тени мягче дефолтных: карточек на экране много, и резкая тень у
          // каждой превращает страницу в рябь.
          boxShadowTertiary: dark
            ? "0 1px 2px rgba(0,0,0,0.32), 0 4px 12px rgba(0,0,0,0.28)"
            : "0 1px 2px rgba(16,24,40,0.04), 0 4px 16px rgba(16,24,40,0.06)",
          wireframe: false,
        },
        components: {
          Layout: {
            headerHeight: 52,
            headerPadding: "0 16px",
            // Дефолтный `#001529` захардкожен в antd и не реагирует на тему.
            siderBg: dark ? "#111318" : "#141a26",
            headerBg: "transparent",
            bodyBg: dark ? "#0b0d11" : "#f4f6fa",
          },
          Menu: {
            itemHeight: 36,
            itemMarginInline: 8,
            itemBorderRadius: 8,
            itemMarginBlock: 2,
            darkItemBg: "transparent",
            darkSubMenuItemBg: "transparent",
          },
          Table: {
            cellPaddingBlock: 9,
            cellPaddingInline: 12,
            headerBorderRadius: 0,
            headerSplitColor: "transparent",
          },
          Card: {
            paddingLG: 16,
          },
          Statistic: {
            contentFontSize: 22,
            titleFontSize: 12,
          },
          Descriptions: {
            itemPaddingBottom: 8,
          },
          Segmented: {
            itemSelectedBg: dark ? "#2b3240" : "#ffffff",
            trackPadding: 2,
          },
          Tag: {
            borderRadiusSM: 6,
          },
        },
      }}
    >
      <AntdApp style={{ height: "100%" }}>{children}</AntdApp>
    </ConfigProvider>
  );
}
